import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const articles = [
  {
    title: "Co-ops: the new ESG investment vehicle for SMSF portfolios",
    author: "Ashleigh Nichols",
    excerpt: "Responsible Investing using Environmental, Social and Governance criteria is an opportunity to extend an investment beyond a purely financial transaction.",
    image: "/media/shutterstock_770002474-1200x560.jpg.webp",
    link: "https://smsfconnect.com/article/co-ops-the-new-esg-investment-vehicle-for-smsf-portfolios",
  },
  {
    title: "Green growth: Australian landscape for renewable energy investments",
    author: "Jane Lawrence",
    excerpt: "Global political action, extreme weather events, technological breakthroughs and changing consumer sentiments have shifted climate-related risks into an investment fundamental for all.",
    image: "/media/shutterstock_2051822582-1200x560.jpg.webp",
    link: "https://smsfconnect.com/article/green-growth-australian-landscape-for-renewable-energy-investments",
  },
  {
    title: "Socially Responsible Investment",
    author: "Jane Lawrence",
    excerpt: "Collectively, ethical investments with an emphasis on clean and green energy projects have the potential to fund Australia's transition to 100% renewable energy.",
    image: "/media/AdobeStock_227727671.webp",
    link: "/socially-responsible-investment",
  },
  {
    title: "Why Invest in Renewable Energy?",
    author: "Jane Lawrence",
    excerpt: "A look at why a community-scale renewable energy project should be at the top of your investment list.",
    image: "/media/pexels-cristian-rojas-8853511.jpg",
    link: "/why-invest-in-renewable-energy",
  },
  {
    title: "Why Join a Renewable Energy Cooperative?",
    author: "Jane Lawrence",
    excerpt: "How energy cooperatives are accelerating the movement toward clean energy.",
    image: "/media/AdobeStock_158281524.webp",
    link: "/why-join-renewable-cooperative",
  },
];

const useReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1 });

    if (currentRef) observer.observe(currentRef);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const NewsAndEvents = () => {
  const [articlesRef, articlesVisible] = useReveal();
  const [eventsRef, eventsVisible] = useReveal();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 my-8">
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
        {/* Featured Articles Section */}
        <div ref={articlesRef} className="lg:w-2/3">
          <h2 className={`text-3xl text-[#ff8400] leading-tight mb-6 transition-all duration-700 ${articlesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>Featured articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {articles.map((article, index) => (
              <div
                key={article.title}
                className={`h-full transition-all duration-700 ${articlesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: articlesVisible ? `${150 + index * 100}ms` : '0ms' }}
              >
                <ArticleCard {...article} />
              </div>
            ))}
          </div>
        </div>
        
        {/* Events Section */}
        <div ref={eventsRef} className="lg:w-1/3">
          <h2 className={`text-3xl text-[#ff8400] leading-tight mb-6 transition-all duration-700 ${eventsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>Events</h2>
          <div className={`space-y-6 transition-all duration-700 delay-200 ${eventsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <EventCard
              title="2025 OCREP Annual General Meeting"
              date="18th of November, 2025 at 7:30 pm (AEDT)"
              description="Members have been emailed the link to the Teams meeting."
              image="/media/pexels-pixabay-356036.webp"
              link="/media/251128 ED CW-NSW Co-operative Meeting Agenda & DRAFT Minutes.pdf"
              linkText="View Agenda & Minutes"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ArticleCard = ({ title, author, excerpt, image, link }) => (
  <div className="bg-white shadow-md overflow-hidden h-full flex flex-col">
    <img src={image} alt={title} className="w-full h-52 object-cover" />
    <div className="p-5 flex flex-col flex-grow">
      <h3 className="text-xl font-semibold leading-tight mb-2 text-gray-800">{title}</h3>
      <p className="text-base text-gray-600 mb-3">By {author}</p>
      <p className="text-base text-gray-600 leading-relaxed mb-5 flex-grow">{excerpt}</p>
      {link ? (
        link.startsWith('http') ? (
          <a href={link} target="_blank" rel="noopener noreferrer" className="text-[#703aa4] hover:text-[#ff8400] transition duration-300 font-semibold flex items-center">
            Read article <ArrowRight className="ml-2 mt-0.5 h-4 w-4" />
          </a>
        ) : (
          <Link to={link} className="text-[#703aa4] hover:text-[#ff8400] transition duration-300 font-semibold flex items-center">
            Read article <ArrowRight className="ml-2 mt-0.5 h-4 w-4" />
          </Link>
        )
      ) : null}
    </div>
  </div>
);

const EventCard = ({ title, date, description, image, presenter, link, linkText }) => (
  <div className="bg-[#703aa4] text-white shadow-md overflow-hidden">
    <img src={image} alt={title} className="w-full h-52 object-cover" />
    <div className="p-5">
      <h3 className="text-xl font-semibold leading-tight mb-2">{title}</h3>
      <p className="text-base mb-3">{date}</p>
      <p className="text-base leading-relaxed mb-5">{description}</p>
      {presenter && <p className="text-sm mb-4">Presented by {presenter}</p>}
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-[#fed142] hover:text-white transition duration-300 font-semibold flex items-center">
          {linkText || 'Learn more'} <ArrowRight className="ml-2 mt-0.5 h-4 w-4" />
        </a>
      ) : (
        <button type="button" className="text-[#fed142] font-semibold flex items-center">
          Watch the recording <ArrowRight className="ml-2 mt-0.5 h-4 w-4" />
        </button>
      )}
    </div>
  </div>
);

export default NewsAndEvents;
